import * as React from 'react';
import ChartQuery from './ChartQuery.gql';
import { findKey } from '../EkonomiskAnalysQueryUtils';
import {
  AnalysGraphQuery as IAnalysGraphQuery,
  AnalysGraphQueryVariables as IAnalysGraphQueryVariables,
  AnalysGraphQuery_ekgrafresultatperid_columns_actions_params as IParams,
} from './_QueryTypes/AnalysGraphQuery';
import { PointOptionsObject, SeriesBarOptions } from 'highcharts';
import { IFilledLocalAnalys } from '../../Components/EkonomiskAnalysMain/EkonomiskAnalysMain';
import {
  TEDInputAvenyEKAnalys,
  TEDEKRapportDimensionTypEnum,
} from '../../../../../../../globalQueryTypes';
import { TEDMixedType } from '../../../../../../../Types/GraphQLTypes';
import {
  iSumDataToInputSum,
  colToInputCol,
  datesToTimeInterval,
} from '../../Utils/convertUtils';
import { TEDUrvalType } from '../../../../../../../Types/UrvalTypes';
import { IEkonomiskAnalysFilledDates } from '../../Components/AnalysSettingsHandler/AnalysSettingsHandler';
import useHandledQuery from '../../../../../../../Components/QueryComponents/HandledQuery/HandledQueryHook';
import { EkonomiskAnalysContext } from '../../Components/Context/EkonomiskAnalysContext';
import { getForceHelarsJamforelse } from '../../Utils/userInfoUtils';

import type { JSX } from "react";

export type ChartType = 'column' | 'pie' | 'line' | 'bubble';

export interface IAnalysChart {
  title: string;
  categories: string[];
  series: SeriesBarOptions[]; // FIXME: Correct type?
  errorMessage?: string;
  warningMessage?: string;
}

export const ChartActions = {
  EkonomiskAnalys: 'Ekonomisk analys',
};

export interface IParamValue {
  name: string;
  value: string;
}

export interface IDrilldown {
  actionType: string | null;
  params: IParamValue[];
}

interface IProps {
  type?: ChartType;
  id: string | null;
  analys?: IFilledLocalAnalys;
  grundurvalId?: string;
  kodbehorighetId: string;
  dates: IEkonomiskAnalysFilledDates;
  detailedSelection?: TEDUrvalType;
  grafColumns?: number[];
  useDrilldown?: boolean;
  loadingComponent?: JSX.Element;

  children: (data: IAnalysChart) => JSX.Element;
}

function generateAnalys(
  analys?: IFilledLocalAnalys,
  forceHelarsjamforelse: boolean = true
): TEDInputAvenyEKAnalys | null {
  if (!analys) {
    return null;
  }
  const inputAnalys: TEDInputAvenyEKAnalys = {
    name: analys.name,
    rubrik: analys.title,
    kategori: analys.category,
    typ: analys.type as TEDEKRapportDimensionTypEnum,
    sums: analys.sums.map(iSumDataToInputSum),
    columns: analys.columns.map(x => colToInputCol(x.data)),
    force_helarsjamforelse: forceHelarsjamforelse,
  };

  return inputAnalys;
}

const FindParamValue = (value: TEDMixedType): string | null => {
  const key = findKey(value);
  if (key) {
    return (value as unknown as Record<string, string>)[key];
  }

  return null;
};

function ParseParams(params: IParams[] | null): IParamValue[] {
  let result: IParamValue[] = [];

  if (params) {
    params.forEach(param => {
      if (param.value) {
        const paramValue = FindParamValue(param.value as TEDMixedType);
        if (paramValue != null) {
          result.push({
            name: param.name,
            value: paramValue,
          });
        }
      }
    });
  }
  return result;
}

const EkonomiskAnalysChartQuery = (props: IProps) => {
  const ekonomiskAnalysContext = React.useContext(EkonomiskAnalysContext);
  let analys: IAnalysChart = {
    title: '',
    categories: [],
    series: [],
  };

  const graftyp = props.type && props.type === 'line' ? 'column' : props.type;
  const queryResult = useHandledQuery<
    IAnalysGraphQuery,
    IAnalysGraphQueryVariables
  >(
    ChartQuery,
    {
      variables: {
        graftyp,
        id: props.id ?? '',
        analys: generateAnalys(
          props.analys,
          getForceHelarsJamforelse(
            ekonomiskAnalysContext.harPeriodiseradBudget,
            ekonomiskAnalysContext.userInfo?.preferHelarsjamforelse
          )
        ),
        grundurvalId: props.grundurvalId,
        kodbehorighetId: props.kodbehorighetId,
        tidsintervall: datesToTimeInterval(props.dates),
        userSelection: props.detailedSelection ? props.detailedSelection : [],
        grafColumns: props.grafColumns
          ? props.grafColumns.map(column => column.toString())
          : props.grafColumns,
      },
      skip: props.id == null,
    },
    {
      loadingComponent: props.loadingComponent
        ? props.loadingComponent
        : undefined,
    }
  );

  if (props.id === null) {
    return <React.Fragment>{props.children(analys)}</React.Fragment>;
  }

  if (!queryResult.data) {
    return queryResult.renderComponent;
  }

  const ekanalysresultat = queryResult.data.ekgrafresultatperid;

  if (ekanalysresultat) {
    if (ekanalysresultat.felmeddelande) {
      analys.errorMessage = ekanalysresultat.felmeddelande;
      return props.children(analys);
    } else if (ekanalysresultat.varningsmeddelande) {
      analys.warningMessage = ekanalysresultat.varningsmeddelande;
    }

    analys.title = ekanalysresultat.title || '';
    analys.categories = ekanalysresultat.columns
      ? ekanalysresultat.columns.map(x => x.rubrik || '')
      : [];
    /*TODO: Only handling one level deepth of sum object*/
    let sum = ekanalysresultat.sums ? ekanalysresultat.sums[0] : null;
    if (sum) {
      if (sum.data) {
        const key = findKey(sum.data as TEDMixedType);

        if (key) {
          let sumData: string[] = (sum.data as unknown as Record<string, string[]>)[key];

          sumData.forEach((x: string) => {
            // FIXME: typning
            (analys.series as any).push({
              name: x,
              data: [],
            });
          });
        }
      }

      if (ekanalysresultat.columns) {
        ekanalysresultat.columns.forEach(x => {
          if (x.data) {
            const key = findKey(x.data as TEDMixedType);

            if (key) {
              const data: string[] = (x.data as unknown as Record<string, string[]>)[key];

              data.forEach((d: string, i: number) => {
                if (analys.series[i] && analys.series[i].data) {
                  const currentAction = x.actions ? x.actions[i] : null;

                  let drilldown: IDrilldown | null = null;
                  if (props.useDrilldown && currentAction) {
                    const parsedParams = ParseParams(currentAction.params);

                    drilldown = {
                      actionType: currentAction.name,
                      params: parsedParams,
                    };
                  }
                  (analys.series[i].data as PointOptionsObject[]).push({
                    name: x.rubrik || '',
                    y:
                      d === null
                        ? 0
                        : props.type === 'pie'
                        ? Math.abs(parseFloat(d))
                        : parseFloat(d),
                    drilldown: props.useDrilldown
                      ? JSON.stringify(drilldown)
                      : undefined,
                  });
                }
              });
            }
          }
        });
      }
    }
  }

  return props.children(analys);
};

export default EkonomiskAnalysChartQuery;
